import { useCallback, useEffect, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Clock,
  Cpu,
  RefreshCw,
  Smartphone,
  X,
  Zap,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AppHealthData,
  AppHealthHistogramBucket,
  DiagnosticEntry,
  diagnosticsApi,
} from '../api/client';
import DiagnosticDetailModal from '../components/DiagnosticDetailModal';
import { cn, formatNumber, formatRelativeTime } from '../lib/utils';

const RECENT_INCIDENT_LIMIT = 12;

function formatDuration(milliseconds: number | null): string {
  if (milliseconds === null) return 'No data';
  if (milliseconds >= 1000) return `${(milliseconds / 1000).toFixed(1)}s`;
  return `${Math.round(milliseconds)}ms`;
}

function formatMemory(megabytes: number | null): string {
  return megabytes === null ? 'No data' : `${megabytes.toFixed(1)} MB`;
}

function histogramData(buckets: AppHealthHistogramBucket[]) {
  return buckets.map(bucket => ({
    range: bucket.end_ms === null
      ? `${formatDuration(bucket.start_ms)}+`
      : `${formatDuration(bucket.start_ms)}–${formatDuration(bucket.end_ms)}`,
    count: bucket.count,
  }));
}

const chartTooltipStyle = {
  backgroundColor: 'hsl(222 47% 10%)',
  border: '1px solid hsl(222 30% 20%)',
  borderRadius: '12px',
  boxShadow: '0 10px 40px hsl(0 0% 0% / 0.3)',
};

export default function AppHealth() {
  const [health, setHealth] = useState<AppHealthData | null>(null);
  const [incidents, setIncidents] = useState<DiagnosticEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailEntry, setDetailEntry] = useState<DiagnosticEntry | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    setError(null);
    try {
      const [healthResult, incidentResult] = await Promise.all([
        diagnosticsApi.health(),
        diagnosticsApi.list({
          diagnostic_kind: 'crash_or_hang',
          limit: RECENT_INCIDENT_LIMIT,
        }),
      ]);
      setHealth(healthResult);
      setIncidents(incidentResult.diagnostics);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to load App Health');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleViewDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      const result = await diagnosticsApi.get(id);
      setDetailEntry(result.diagnostic);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to load diagnostic report');
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading App Health...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-4 animate-fade-in-up">
        <div>
          <div className="flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-cyan-400" />
            <h1 className="text-3xl font-bold text-gradient">App Health</h1>
          </div>
          <p className="text-muted-foreground mt-2">
            MetricKit reliability and performance across retained reports
          </p>
        </div>
        <button
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="btn-secondary px-4 py-2 rounded-xl flex items-center gap-2 text-sm"
        >
          <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="alert-error p-3 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto" aria-label="Dismiss error">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {health ? (
        <>
          <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 px-4 py-3 text-sm text-cyan-100/80">
            Payload-derived counts and charts are based on at most the last {formatNumber(health.sample_limit)} reports per type:
            {' '}{formatNumber(health.sampled.diagnostic_reports)} valid, non-debug diagnostic and {formatNumber(health.sampled.metric_reports)} valid, non-debug metric reports.
            Totals, versions, and the {health.trend_weeks}-week trend use retained metadata and may include legacy debug rows without scanning their payloads.
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
            <HealthCard
              title="Total Reports"
              value={formatNumber(health.totals.reports)}
              detail={`${formatNumber(health.totals.metrics)} metric · ${formatNumber(health.totals.diagnostics)} diagnostic`}
              icon={Activity}
              colorClass="stat-card-cyan"
              iconColor="text-cyan-400"
            />
            <HealthCard
              title="Devices"
              value={formatNumber(health.totals.devices)}
              detail="Across all retained reports"
              icon={Smartphone}
              colorClass="stat-card-violet"
              iconColor="text-violet-400"
            />
            <HealthCard
              title="Crashes"
              value={formatNumber(health.diagnostic_counts.crashes)}
              detail={`Last ${formatNumber(health.sample_limit)} diagnostic reports`}
              icon={Zap}
              colorClass="stat-card-amber"
              iconColor="text-rose-400"
            />
            <HealthCard
              title="Worst Hang"
              value={formatDuration(health.worst_hang_ms)}
              detail={`Last ${formatNumber(health.sample_limit)} diagnostic reports`}
              icon={Clock}
              colorClass="stat-card-emerald"
              iconColor="text-amber-400"
            />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <section className="xl:col-span-2 card-glow rounded-2xl p-6">
              <div className="mb-5">
                <h2 className="text-lg font-semibold">Weekly Report Trend</h2>
                <p className="text-sm text-muted-foreground">Retained metadata over the last {health.trend_weeks} weeks</p>
              </div>
              <div className="h-72">
                {health.trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={health.trend}>
                      <defs>
                        <linearGradient id="metricReports" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.35} />
                          <stop offset="95%" stopColor="#22d3ee" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="diagnosticReports" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 30% 16%)" vertical={false} />
                      <XAxis dataKey="week_start" tickFormatter={value => value.slice(5)} stroke="hsl(215 20% 45%)" fontSize={12} tickLine={false} axisLine={false} />
                      <YAxis stroke="hsl(215 20% 45%)" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                      <Tooltip contentStyle={chartTooltipStyle} />
                      <Area type="monotone" dataKey="metrics" name="Metric reports" stroke="#22d3ee" fill="url(#metricReports)" strokeWidth={2} />
                      <Area type="monotone" dataKey="diagnostics" name="Diagnostic reports" stroke="#f43f5e" fill="url(#diagnosticReports)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyChart />
                )}
              </div>
            </section>

            <section className="card-glow rounded-2xl p-6">
              <div className="mb-5">
                <h2 className="text-lg font-semibold">Diagnostic Events</h2>
                <p className="text-sm text-muted-foreground">From the bounded diagnostic sample</p>
              </div>
              <div className="space-y-4">
                <CountRow label="Crashes" value={health.diagnostic_counts.crashes} color="bg-rose-500" />
                <CountRow label="Hangs" value={health.diagnostic_counts.hangs} color="bg-amber-500" />
                <CountRow label="Disk write exceptions" value={health.diagnostic_counts.disk_writes} color="bg-violet-500" />
                <CountRow label="CPU exceptions" value={health.diagnostic_counts.cpu_exceptions} color="bg-cyan-500" />
              </div>
            </section>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <HistogramCard title="Hang Duration" subtitle="Application responsiveness metrics" buckets={health.hang_histogram} color="#f59e0b" />
            <HistogramCard title="Launch Time" subtitle="Time to first draw metrics" buckets={health.launch_histogram} color="#22d3ee" />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <section className="card-glow rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-5">
                <Cpu className="w-5 h-5 text-violet-400" />
                <div>
                  <h2 className="text-lg font-semibold">Peak Memory</h2>
                  <p className="text-sm text-muted-foreground">{formatNumber(health.peak_memory.reports)} metric reports with data</p>
                </div>
              </div>
              <div className="space-y-4">
                <MemoryRow label="Average" value={formatMemory(health.peak_memory.average_mb)} />
                <MemoryRow label="Median" value={formatMemory(health.peak_memory.median_mb)} />
                <MemoryRow label="Maximum" value={formatMemory(health.peak_memory.max_mb)} />
              </div>
            </section>

            <section className="xl:col-span-2 card-glow rounded-2xl overflow-hidden">
              <div className="p-6 pb-3">
                <h2 className="text-lg font-semibold">Reports by App Version</h2>
                <p className="text-sm text-muted-foreground">All retained rows and unique devices</p>
              </div>
              <div className="overflow-x-auto">
                <table className="table-neural min-w-full">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Version</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase">Reports</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase">Devices</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/30">
                    {health.by_app_version.map(version => (
                      <tr key={version.app_version}>
                        <td className="px-6 py-3 font-medium">v{version.app_version}</td>
                        <td className="px-6 py-3 text-right text-gray-300">{formatNumber(version.rows)}</td>
                        <td className="px-6 py-3 text-right text-gray-400">{formatNumber(version.devices)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>

          <section className="card-glow rounded-2xl overflow-hidden">
            <div className="p-6 pb-3">
              <h2 className="text-lg font-semibold">Recent Crash & Hang Reports</h2>
              <p className="text-sm text-muted-foreground">Select a report to open its full MetricKit payload</p>
            </div>
            {incidents.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="table-neural min-w-full">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Received</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Kind</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Version</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Device</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/30">
                    {incidents.map(incident => (
                      <tr
                        key={incident.id}
                        className="hover:bg-white/[0.02] cursor-pointer transition"
                        onClick={() => handleViewDetail(incident.id)}
                      >
                        <td className="px-6 py-3 text-sm text-gray-300 whitespace-nowrap">{formatRelativeTime(new Date(incident.created_at).getTime())}</td>
                        <td className="px-6 py-3">
                          <div className="flex gap-2">
                            {(incident.diagnostic_kinds || []).map(kind => (
                              <span
                                key={kind}
                                className={cn(
                                  'px-2 py-0.5 rounded-full border text-xs font-medium',
                                  kind === 'crash'
                                    ? 'bg-rose-500/20 text-rose-400 border-rose-500/30'
                                    : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                                )}
                              >
                                {kind}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-3 text-sm text-gray-400">v{incident.app_version || 'unknown'}</td>
                        <td className="px-6 py-3 text-sm text-gray-400 font-mono">{incident.device_model || 'Unknown'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-12 text-center text-muted-foreground">No recent crash or hang reports</div>
            )}
          </section>
        </>
      ) : (
        <div className="card-glow rounded-2xl p-12 text-center text-muted-foreground">
          App Health data is unavailable. Refresh to try again.
        </div>
      )}

      <DiagnosticDetailModal
        entry={detailEntry}
        loading={detailLoading}
        onClose={() => setDetailEntry(null)}
      />
    </div>
  );
}

function HealthCard({
  title,
  value,
  detail,
  icon: Icon,
  colorClass,
  iconColor,
}: {
  title: string;
  value: string;
  detail: string;
  icon: typeof Activity;
  colorClass: string;
  iconColor: string;
}) {
  return (
    <div className={cn('card-glow rounded-2xl p-6', colorClass)}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-muted-foreground">{title}</span>
        <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
          <Icon className={cn('w-5 h-5', iconColor)} />
        </div>
      </div>
      <p className="text-3xl font-bold">{value}</p>
      <p className="text-xs text-muted-foreground mt-2">{detail}</p>
    </div>
  );
}

function CountRow({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="flex items-center justify-between rounded-xl bg-secondary/40 px-4 py-3">
      <div className="flex items-center gap-3">
        <span className={cn('w-2.5 h-2.5 rounded-full', color)} />
        <span className="text-sm text-gray-300">{label}</span>
      </div>
      <span className="font-semibold">{formatNumber(value)}</span>
    </div>
  );
}

function HistogramCard({
  title,
  subtitle,
  buckets,
  color,
}: {
  title: string;
  subtitle: string;
  buckets: AppHealthHistogramBucket[];
  color: string;
}) {
  const data = histogramData(buckets);
  return (
    <section className="card-glow rounded-2xl p-6">
      <div className="mb-5">
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
      <div className="h-64">
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 30% 16%)" vertical={false} />
              <XAxis dataKey="range" stroke="hsl(215 20% 45%)" fontSize={11} tickLine={false} axisLine={false} interval="preserveStartEnd" />
              <YAxis stroke="hsl(215 20% 45%)" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={chartTooltipStyle} />
              <Bar dataKey="count" name="Samples" fill={color} radius={[5, 5, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyChart />
        )}
      </div>
    </section>
  );
}

function EmptyChart() {
  return (
    <div className="h-full flex flex-col items-center justify-center text-muted-foreground">
      <Activity className="w-8 h-8 mb-2 opacity-40" />
      <span className="text-sm">No metric data in the recent sample</span>
    </div>
  );
}

function MemoryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-border/30 pb-3 last:border-0 last:pb-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}
